/**
 * /venues/:venueSlug/my-stats — the signed-in player's own record at this venue.
 *
 * Inside the auth guard, and venue-scoped on purpose: an account belongs to a
 * person but a season rank belongs to a venue, so this is per venue while
 * /profile is not (see the note on both routes in routes.jsx).
 *
 * Reads /players/:userId/stats with the caller's OWN id, which is the endpoint
 * that ignores profile_hidden — a player who has hidden their profile from other
 * people has not hidden it from themselves — and the only one that will answer
 * for a CLOSED season, which is what makes the selector below possible.
 *
 * THE SELECTOR ONLY APPEARS WHEN THERE IS A CHOICE. One season is the common case
 * for a new venue, and a dropdown holding a single option is furniture.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiGet } from '../lib/api.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useVenuePage } from '../lib/useVenuePage.js';
import PlayerStatsBody from '../components/stats/PlayerStatsBody.jsx';

export default function MyStats() {
  const { venueSlug } = useParams();
  const { user } = useAuth();
  const userId = user?.id;

  // null means "whatever the venue's current season is", which is what the
  // endpoint defaults to. Set to an id once the player picks one.
  const [seasonId, setSeasonId] = useState(null);

  const state = useVenuePage(
    venueSlug,
    (venue) => apiGet(
      `/api/venues/${venue.id}/players/${userId}/stats`
      + (seasonId != null ? `?seasonId=${seasonId}` : '')
    ),
    [userId, seasonId]
  );

  if (state.status === 'loading') {
    return <div className="hd-empty">Loading…</div>;
  }

  if (state.status === 'error') {
    return (
      <div className="hd-stack hd-stats-page">
        <h1 className="hd-page-title">
          {state.venue ? 'Stats unavailable' : 'Venue not found'}
        </h1>
        <p className="hd-muted">
          {state.venue
            ? state.error
            : <>No venue at <code>/{venueSlug}</code>.</>}
          {' '}
          <Link to={state.venue ? `/venues/${venueSlug}` : '/'}>
            {state.venue ? 'Back to the venue' : 'Back to the home page'}
          </Link>
        </p>
      </div>
    );
  }

  const { venue, data } = state;
  const { player, season, allTime, tournaments, seasons } = data;

  return (
    <div className="hd-stack hd-stats-page">
      <div className="hd-stats-head">
        <h1 className="hd-page-title">My stats</h1>
        <p className="hd-meta">
          <Link to={`/venues/${venue.slug}`}>{venue.name}</Link>
          {' · '}{player.displayName}
        </p>
      </div>

      {seasons.length > 1 && (
        <div className="hd-row hd-stats-controls">
          <label className="hd-label" htmlFor="hd-season-pick">Season</label>
          <select
            id="hd-season-pick"
            className="hd-select"
            value={seasonId ?? season?.seasonId ?? ''}
            onChange={(e) => setSeasonId(Number(e.target.value))}
          >
            {/* Only when nothing resolved: the venue has seasons but none is
                active, so there is no option to show as selected and a blank
                select would look broken rather than unset. */}
            {seasonId == null && season == null && (
              <option value="" disabled>Choose a season</option>
            )}
            {seasons.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}{s.status !== 'active' ? ` (${s.status})` : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      <PlayerStatsBody
        venueSlug={venue.slug}
        season={season}
        allTime={allTime}
        tournaments={tournaments}
      />
    </div>
  );
}
